import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { TemplateVersion, SmartContractTemplate } from './useSmartContractTemplates';

export interface CodeDiff {
  version1: string;
  version2: string;
  additions: number;
  deletions: number;
  changes: DiffChange[];
}

export interface DiffChange {
  type: 'add' | 'remove' | 'unchanged';
  lineNumber: number;
  content: string;
}

// API Functions
const fetchVersions = async (templateId: string): Promise<TemplateVersion[]> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${templateId}/versions`);
  return response.data;
};

const fetchVersion = async (templateId: string, versionId: string): Promise<TemplateVersion> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${templateId}/versions/${versionId}`);
  return response.data;
};

const activateVersion = async ({ templateId, versionId }: { templateId: string; versionId: string }): Promise<SmartContractTemplate> => {
  const response = await api.post(`/tokenization/smart-contracts/templates/${templateId}/versions/${versionId}/activate`);
  return response.data;
};

const compareVersions = async ({ templateId, v1, v2 }: { templateId: string; v1: string; v2: string }): Promise<CodeDiff> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${templateId}/diff?v1=${v1}&v2=${v2}`);
  return response.data;
};

// Hooks
export function useVersions(templateId: string | undefined) {
  return useQuery({
    queryKey: ['template-versions', templateId],
    queryFn: () => fetchVersions(templateId!),
    enabled: !!templateId,
  });
}

export function useVersion(templateId: string | undefined, versionId: string | undefined) {
  return useQuery({
    queryKey: ['template-version', templateId, versionId],
    queryFn: () => fetchVersion(templateId!, versionId!),
    enabled: !!templateId && !!versionId,
  });
}

export function useActivateVersion() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: activateVersion,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['template-versions', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['smart-contract-template', variables.templateId] });
    },
  });
}

export function useVersionDiff(templateId: string | undefined, v1: string | undefined, v2: string | undefined) {
  return useQuery({
    queryKey: ['template-version-diff', templateId, v1, v2],
    queryFn: () => compareVersions({ templateId: templateId!, v1: v1!, v2: v2! }),
    enabled: !!templateId && !!v1 && !!v2,
  });
}

// Export all hooks as a single object
export const useTemplateVersions = {
  useVersions,
  useVersion,
  useActivateVersion,
  useVersionDiff,
};

export default useTemplateVersions;
